'use client';

import React, { useState, useRef } from 'react';
import TransitionLink from '@/components/TransitionLink';
import { isFilled } from '@prismicio/client';
import { PrismicNextImage } from '@prismicio/next';
import type { ImageField } from '@prismicio/client';
import SplashButton from '@/components/SplashButton';

type IntegrationCard = {
  uid: string;
  title: string;
  description: string;
  image: ImageField;
  integration_mark: ImageField;
  integration_teaser: string;
  tags: string[];
  lang: string;
};

type RelatedIntegrationsProps = {
  integrations: IntegrationCard[];
  lang: string;
  category?: string;
};

const PAGE_SIZE = 6;

const RelatedIntegrations = ({ integrations, lang, category }: RelatedIntegrationsProps) => {
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const prevVisibleCount = useRef(PAGE_SIZE);

  if (integrations.length === 0) return null;

  const hasMore = integrations.length > visibleCount;
  const visibleCards = integrations.slice(0, visibleCount);

  return (
    <section className="bg-gradient-to-b from-white to-blue-50 py-20 pt-12 sm:py-24 ">
      <div className="container mx-auto">
        <h3 className="mb-8 text-center text-3xl font-normal leading-tight text-gray-900 lg:text-4xl md:font-light">
          {category ? (
            <>Popular <span className="text-yellow-600">{category}</span> Integrations</>
          ) : (
            'Related Integrations'
          )}
        </h3>

        <div className="relative">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {visibleCards.map((card, i) => {
              const displayTitle = card.title
                .replace(/\s*\+\s*NetSuite\s+Integration$/i, '')
                .replace(/\s*NetSuite\s+Integration$/i, '');
              const staggerIndex = i < prevVisibleCount.current ? i : i - prevVisibleCount.current;

              return (
                <TransitionLink
                  key={card.uid}
                  href={`/${card.lang}/integrations/${card.uid}`}
                  className="group flex items-start gap-4 rounded-xl border border-gray-300 bg-white p-5 shadow-md transition-all duration-200 hover:border-blue-300 hover:border-dashed hover:bg-gray-50/20 hover:shadow-none"
                  style={{
                    animation: 'fadeInUp 0.3s ease-out both',
                    animationDelay: `${staggerIndex * 50}ms`,
                  }}
                >
                  <div className="flex h-20 w-20 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg border-none bg-white p-1 transition-all group-hover:border group-hover:shadow-md">
                    {isFilled.image(card.integration_mark) ? (
                      <PrismicNextImage
                        field={card.integration_mark}
                        className="h-full w-full object-contain"
                        width={80}
                        height={80}
                      />
                    ) : isFilled.image(card.image) ? (
                      <PrismicNextImage
                        field={card.image}
                        className="h-full w-full object-contain"
                        width={80}
                        height={80}
                      />
                    ) : (
                      <svg className="h-8 w-8 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                      </svg>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 leading-snug">
                      {displayTitle}
                    </h4>
                    <p className="mt-1 text-sm text-gray-500 group-hover:text-gray-600">
                      {card.integration_teaser || card.description}
                    </p>
                  </div>
                </TransitionLink>
              );
            })}
          </div>

          {/* Hidden links for cards beyond current page — SEO only, no images loaded */}
          {hasMore && (
            <div className="hidden" aria-hidden="true">
              {integrations.slice(visibleCount).map((card) => (
                <TransitionLink
                  key={card.uid}
                  href={`/${card.lang}/integrations/${card.uid}`}
                  tabIndex={-1}
                >
                  {card.title}
                </TransitionLink>
              ))}
            </div>
          )}
        </div>

        <div className="mt-8 flex flex-col items-center gap-4 sm:flex-row sm:justify-between">
          <p className="text-center sm:text-left text-sm text-gray-500 mb-4 sm:mb-0">
            Showing {visibleCards.length} of {integrations.length}{category ? ` ${category}` : ''} Integrations
          </p>
          {hasMore && (
            <SplashButton
              as="button"
              text="Discover More"
              className="min-w-[300px]"
              color="blue"
              size="lg"
              icon="down"
              onClick={() => {
                prevVisibleCount.current = visibleCount;
                setVisibleCount((prev) => prev + PAGE_SIZE);
              }}
            />
          )}
        </div>
      </div>
    </section>
  );
};

export default RelatedIntegrations;
